import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trophy, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { downloadCertificatePdf } from "@/lib/pdf";
import * as XLSX from "xlsx-js-style";

const CERT_TYPE_LABELS = { semester1: "الفصل الدراسي الأول", semester2: "الفصل الدراسي الثاني", end_year: "نهاية العام" };
const TOP_N = 10;

/**
 * TopStudentsPrint — طباعة العشرة الأوائل بحسب الصف والفصل (أو كل
 * الفصول)، بنفس تصميم قوائم الفصول، مع تصدير PDF وExcel معًا.
 */
export default function TopStudentsPrint() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const [grade, setGrade] = useState("");
  const [cls, setCls] = useState("__all__");
  const [certType, setCertType] = useState("semester1");
  const [classes, setClasses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!grade) { setClasses([]); return; }
    api.get("/students/classes", { params: { stage: ctx.stage, year: ctx.year, grade } })
      .then(({ data }) => setClasses(data)).catch(() => {});
  }, [ctx.stage, ctx.year, grade]);

  const fetchTop = async () => {
    const { data } = await api.get("/reports/rank", {
      params: {
        stage: ctx.stage, year: ctx.year, grade, cert_type: certType,
        ...(cls !== "__all__" ? { class_name: cls } : {}),
      },
    });
    return data.slice(0, TOP_N);
  };

  const generatePdf = async () => {
    if (!grade) return toast.error("اختر الصف أولاً");
    setGenerating(true);
    try {
      const top = await fetchTop();
      if (!top.length) { toast.error("لا يوجد طلاب في هذا التحديد"); return; }

      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.direction = "rtl";
      container.style.background = "#ffffff";
      document.body.appendChild(container);

      const wrap = document.createElement("div");
      wrap.style.width = "794px";
      wrap.style.padding = "20px";
      wrap.style.background = "#ffffff";
      container.appendChild(wrap);

      const { createRoot } = await import("react-dom/client");
      const root = createRoot(wrap);
      await new Promise((resolve) => {
        root.render(
          <TopStudentsSheet school={settings} stage={ctx.stage} year={ctx.year}
                            grade={grade} cls={cls === "__all__" ? "الكل" : cls}
                            certType={certType} students={top} />
        );
        document.fonts.ready.then(() => setTimeout(resolve, 150));
      });
      await downloadCertificatePdf(wrap, `العشرة-الأوائل-${grade}-${cls === "__all__" ? "الكل" : cls}.pdf`);
      container.remove();
      toast.success("تم توليد ملف PDF بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const generateExcel = async () => {
    if (!grade) return toast.error("اختر الصف أولاً");
    setGenerating(true);
    try {
      const top = await fetchTop();
      if (!top.length) { toast.error("لا يوجد طلاب في هذا التحديد"); return; }

      const HEADER_TEAL = "0D9488";
      const GRID_COLOR = "94A3B8";
      const thinGrid = { style: "thin", color: { rgb: GRID_COLOR } };
      const border = { top: thinGrid, bottom: thinGrid, left: thinGrid, right: thinGrid };
      const headerFont = { color: { rgb: "FFFFFF" }, bold: true, sz: 11 };
      const centerAlign = { horizontal: "center", vertical: "center" };

      const schoolLines = [
        settings?.ministry_name || "وزارة التعليم",
        ...(settings?.education_dept ? [settings.education_dept] : []),
        ...(settings?.office_name ? [settings.office_name] : []),
        settings?.school_name || "المدرسة",
      ];

      const aoa = [
        ...schoolLines.map((l) => [l]),
        [`العشرة الأوائل — الصف: ${grade} — الفصل: ${cls === "__all__" ? "الكل" : cls} — ${CERT_TYPE_LABELS[certType]}`],
        [],
        ["م", "اسم الطالب", "الفصل", "الدرجة", "الترتيب"],
        ...top.map((s, i) => [i + 1, s.name, s.class, s.total, s.rank]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!rtl"] = true;
      const totalCols = 5;
      const headerRowIdx = schoolLines.length + 2;
      const merges = [];
      for (let i = 0; i < schoolLines.length; i++) merges.push({ s: { r: i, c: 0 }, e: { r: i, c: totalCols - 1 } });
      merges.push({ s: { r: schoolLines.length, c: 0 }, e: { r: schoolLines.length, c: totalCols - 1 } });
      ws["!merges"] = merges;

      const setCell = (r, c, style) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" };
        ws[addr].s = { ...(ws[addr].s || {}), ...style };
      };
      schoolLines.forEach((_, i) => setCell(i, 0, { font: { bold: true, sz: i === schoolLines.length - 1 ? 13 : 11 }, alignment: { horizontal: "center" } }));
      setCell(schoolLines.length, 0, { font: { italic: true, sz: 11 }, alignment: { horizontal: "center" } });
      for (let c = 0; c < totalCols; c++) {
        setCell(headerRowIdx, c, { fill: { patternType: "solid", fgColor: { rgb: HEADER_TEAL } }, font: headerFont, alignment: centerAlign, border });
      }
      top.forEach((_, i) => {
        const rowIdx = headerRowIdx + 1 + i;
        const stripe = i % 2 === 1 ? "F1F5F9" : "FFFFFF";
        for (let c = 0; c < totalCols; c++) {
          setCell(rowIdx, c, { fill: { patternType: "solid", fgColor: { rgb: stripe } }, alignment: centerAlign, border });
        }
      });

      ws["!cols"] = [{ wch: 8 }, { wch: 28 }, { wch: 14 }, { wch: 10 }, { wch: 10 }];
      ws["!freeze"] = { xSplit: 0, ySplit: headerRowIdx + 1 };

      const wb = XLSX.utils.book_new();
      wb.Workbook = { Views: [{ RTL: true }] };
      XLSX.utils.book_append_sheet(wb, ws, "العشرة الأوائل");
      XLSX.writeFile(wb, `العشرة-الأوائل-${grade}-${cls === "__all__" ? "الكل" : cls}.xlsx`);
      toast.success("تم تصدير ملف Excel بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="topstudents-page">
      <PageHeader
        icon={Trophy}
        overline="التقارير والشهادات"
        title="طباعة العشرة الأوائل"
        subtitle="أفضل عشرة طلاب بحسب الصف والفصل (أو كل الفصول)، مع تصدير PDF وExcel."
      />

      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[12px]">الصف</Label>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger className="h-10" data-testid="ts-grade"><SelectValue placeholder="اختر الصف" /></SelectTrigger>
            <SelectContent>
              {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[12px]">الفصل</Label>
          <Select value={cls} onValueChange={setCls} disabled={!grade}>
            <SelectTrigger className="h-10" data-testid="ts-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">كل الفصول</SelectItem>
              {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[12px]">الفترة</Label>
          <Select value={certType} onValueChange={setCertType}>
            <SelectTrigger className="h-10" data-testid="ts-certtype"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semester1">الفصل الأول</SelectItem>
              <SelectItem value="semester2">الفصل الثاني</SelectItem>
              <SelectItem value="end_year">نهاية العام</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button variant="outline" onClick={generateExcel} disabled={!grade || generating} className="h-10 flex-1" data-testid="ts-excel">
            <FileSpreadsheet className="h-4 w-4 me-1.5" /> Excel
          </Button>
          <Button onClick={generatePdf} disabled={!grade || generating} className="h-10 flex-1" data-testid="ts-pdf">
            <FileDown className="h-4 w-4 me-1.5" /> {generating ? "جارٍ التوليد…" : "PDF"}
          </Button>
        </div>
      </div>

      {!grade && (
        <div className="rounded-xl border border-dashed border-border bg-white p-16 text-center text-muted-foreground">
          اختر الصف لعرض خيارات الطباعة
        </div>
      )}
    </div>
  );
}

/** ورقة العشرة الأوائل — بنفس تصميم قوائم الفصول تمامًا. */
function TopStudentsSheet({ school, stage, year, grade, cls, certType, students }) {
  return (
    <div className="mx-auto max-w-4xl bg-white border-4 border-primary/70 rounded-xl p-8 shadow-sm">
      <div className="border-b-2 border-primary/50 pb-4 text-center space-y-1 relative">
        {school?.ministry_logo && (
          <img src={school.ministry_logo} alt="شعار الوزارة"
               className="absolute top-0 start-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
        )}
        {school?.school_logo && (
          <img src={school.school_logo} alt="شعار المدرسة"
               className="absolute top-0 end-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
        )}
        <div className="text-primary font-display text-xl font-bold">{school?.ministry_name || "وزارة التعليم"}</div>
        {school?.education_dept && <div className="text-primary font-medium">{school.education_dept}</div>}
        {school?.office_name && <div className="text-primary font-medium">{school.office_name}</div>}
        <div className="text-primary font-display text-lg font-bold">{school?.school_name || "المدرسة"}</div>
      </div>

      <div className="mt-6 text-center">
        <h2 className="font-display text-lg font-bold text-primary">العشرة الأوائل</h2>
        <div className="mt-2 text-sm">
          {stage} — الصف: {grade} — الفصل: {cls} — {CERT_TYPE_LABELS[certType]}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">العام الدراسي: {year}</div>
      </div>

      <table className="w-full mt-6 text-sm border-collapse">
        <thead>
          <tr className="bg-primary text-white">
            <th className="px-2 py-2 text-center border border-primary w-12">م</th>
            <th className="px-2 py-2 text-start border border-primary">اسم الطالب</th>
            <th className="px-2 py-2 text-center border border-primary w-24">الفصل</th>
            <th className="px-2 py-2 text-center border border-primary w-20">الدرجة</th>
            <th className="px-2 py-2 text-center border border-primary w-20">الترتيب</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s, i) => (
            <tr key={s.student_id || i}>
              <td className="px-2 py-2 border border-slate-200 text-center">{i + 1}</td>
              <td className="px-2 py-2 border border-slate-200">{s.name}</td>
              <td className="px-2 py-2 border border-slate-200 text-center">{s.class}</td>
              <td className="px-2 py-2 border border-slate-200 text-center tabular-nums">{s.total}</td>
              <td className="px-2 py-2 border border-slate-200 text-center font-bold">{s.rank}</td>
            </tr>
          ))}
          {students.length === 0 && (
            <tr><td colSpan={5} className="px-2 py-6 border border-slate-200 text-center text-muted-foreground">
              لا يوجد طلاب في هذا التحديد
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
